import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from './config/axios';
import { AuthContext } from './contexts/AuthContext';
import Login from './components/Login';
import UserManagement from './components/UserManagement';
import Lucro from './components/Lucro';
import Relatorios from './components/Relatorios';
import './App.css';

function App() {
  const { user, loading: authLoading } = useContext(AuthContext);
  const [currentView, setCurrentView] = useState('pedidos-a-enviar');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [mensagemModal, setMensagemModal] = useState({
    aberto: false,
    order: null,
    numero: '',
    mensagem: '',
    sending: false,
    error: null,
    success: null
  });

  // Limpa o erro quando o usuário for autenticado
  useEffect(() => {
    if (user) {
      setError(null);
    }
  }, [user]);

  // Persistir meta_connected/meta_error/ga4_connected na carga (URL pode ser perdida antes do user estar pronto)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('meta_connected') === '1') sessionStorage.setItem('meta_redirect', 'connected');
    else if (params.get('meta_error')) sessionStorage.setItem('meta_redirect', 'error');
    if (params.get('ga4_connected') === '1') sessionStorage.setItem('ga4_redirect', '1');
  }, []);

  // Ao voltar do OAuth da Meta (ou quando user fica disponível), abrir a aba Lucro
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('meta_connected') === '1';
    const fromStorage = sessionStorage.getItem('meta_redirect');
    if (fromUrl || fromStorage === 'connected' || fromStorage === 'error') {
      sessionStorage.removeItem('meta_redirect');
      sessionStorage.setItem('meta_show_success', fromStorage === 'error' ? 'error' : 'connected');
      setCurrentView('lucro');
    }
    // Redirect de volta do OAuth GA4 → abre Relatórios (somente admin)
    const fromGa4 = params.get('ga4_connected') === '1' || sessionStorage.getItem('ga4_redirect') === '1';
    if (fromGa4) {
      sessionStorage.removeItem('ga4_redirect');
      window.history.replaceState({}, '', window.location.pathname);
      if (user.is_admin) setCurrentView('relatorios');
    }
  }, [user]);

  // Impede view Relatórios se o usuário não for admin (ex.: estado antigo ou URL manipulada)
  useEffect(() => {
    if (user && !user.is_admin && currentView === 'relatorios') {
      setCurrentView('pedidos-a-enviar');
    }
  }, [user, currentView]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [marcandoEnviado, setMarcandoEnviado] = useState({});
  const [revertendoEnvio, setRevertendoEnvio] = useState({});
  const [imprimindoEtiqueta, setImprimindoEtiqueta] = useState({});
  const [pedidosSelecionados, setPedidosSelecionados] = useState(new Set());
  const [imprimindoMultiplas, setImprimindoMultiplas] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [encomendaModal, setEncomendaModal] = useState({
    aberto: false,
    linhas: [],
    textoPlanilha: '',
    pedidosShopifyIds: [],
    pedidosCount: 0,
    loading: false,
    error: null,
    success: null
  });

  const fetchOrders = useCallback(async (statusFilter = null) => {
    if (!axios.defaults.headers.common['Authorization']) {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      } else {
        return;
      }
    }

    setLoading(true);
    setError(null);
    try {
      const params = statusFilter ? { status_pedido: statusFilter } : {};
      const response = await axios.get('/api/orders', { params });
      setOrders(response.data.data || []);
    } catch (err) {
      // Ignora erro de "Token não fornecido" se o usuário estiver autenticado (pode ser race condition)
      if (err.response?.data?.error === 'Token não fornecido' && user) {
        // Tenta novamente após um pequeno delay
        setTimeout(() => {
          fetchOrders(statusFilter);
        }, 100);
        return;
      }
      setError(err.response?.data?.error || 'Erro ao buscar pedidos');
      console.error('Erro:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const syncOrders = async () => {
    setSyncing(true);
    setError(null);
    try {
      const response = await axios.post('/api/sync/now');
      setSyncStatus(response.data.data);

      // Atualiza a view atual após sincronizar
      if (currentView === 'pedidos-a-enviar') {
        await fetchOrders('not_sent');
      } else if (currentView === 'pedidos-enviados') {
        await fetchOrders('Enviado');
      }

      setTimeout(() => setSyncStatus(null), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao sincronizar pedidos');
      console.error('Erro:', err);
    } finally {
      setSyncing(false);
    }
  };

  const gerarDeclaracaoConteudo = (pedido, etiquetaUrl) => {
    const remetente = {
      nome: 'Ismael Pereira Santos de Melo',
      endereco: 'Rua Professora Carolina Ribeiro, 91',
      cidade: 'São Paulo',
      uf: 'SP',
      cep: '04116-020'
    };

    const destinatario = pedido.shipping_address || {};
    const enderecoDest = destinatario.address || [
      destinatario.address1 || '',
      destinatario.address2 || ''
    ].filter(Boolean).join(', ');

    // Remove "#XX" do nome do destinatário se existir
    const nomeDestinatario = (destinatario.name || pedido.customer_name || '').replace(/\s*-\s*#\d+\s*$/, '').trim();

    // Formata CEP (adiciona hífen se necessário)
    const formatarCEP = (cep) => {
      if (!cep) return '';
      const cepLimpo = cep.replace(/\D/g, '');
      if (cepLimpo.length === 8) {
        return `${cepLimpo.slice(0, 5)}-${cepLimpo.slice(5)}`;
      }
      return cep;
    };

    const cepFormatado = formatarCEP(destinatario.zip || '');

    const produtos = pedido.products || [];
    const totalValor = produtos.reduce((sum, p) => sum + (p.price * p.quantity), 0);

    const hoje = new Date();
    const dia = hoje.getDate().toString().padStart(2, '0');
    const ano = hoje.getFullYear();
    const meses = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
      'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
    const mesNome = meses[hoje.getMonth()];

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Declaração de Conteúdo</title>
          <style>
            @page {
              size: A4 portrait;
              margin: 10mm;
            }
            @media print {
              @page {
                size: A4 portrait;
              }
              body { margin: 2%; padding: 0; }
              .page-break { page-break-after: always; }
            }
            body {
              font-family: Arial, sans-serif;
              font-size: 8px;
              margin: 2%;
              padding: 0;
            }
            .container {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 8px;
              max-height: 40vh;
              align-items: start;
            }
            .etiqueta {
              max-height: 40vh;
              overflow: hidden;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: flex-start;
            }
            .etiqueta img {
              max-width: 100%;
              max-height: 40vh;
              height: auto;
              object-fit: contain;
            }
            .declaracao {
              border: 2px solid #000;
              padding: 6.4px;
              max-height: 40vh;
              overflow: hidden;
              font-size: 7.2px;
            }
            h1 {
              text-align: center;
              font-size: 8.8px;
              font-weight: bold;
              margin: 4px 0 6.4px 0;
              text-transform: uppercase;
            }
            .remetente-destinatario {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 6.4px;
              margin-bottom: 6.4px;
            }
            .coluna {
              border: 1px solid #000;
              padding: 4px;
            }
            .coluna h3 {
              font-size: 7.2px;
              font-weight: bold;
              margin: 0 0 4px 0;
              text-transform: uppercase;
            }
            .campo {
              margin-bottom: 3.2px;
            }
            .campo label {
              font-weight: bold;
              display: block;
              margin-bottom: 0.8px;
              font-size: 6.4px;
            }
            .campo .valor {
              border-bottom: 1px solid #000;
              min-height: 9.6px;
              padding: 0.8px 0;
              font-size: 6.4px;
            }
            .identificacao-bens {
              margin-top: 6.4px;
            }
            .identificacao-bens h3 {
              font-size: 7.2px;
              font-weight: bold;
              margin: 0 0 4px 0;
              text-transform: uppercase;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 6.4px;
              font-size: 5.6px;
            }
            table th, table td {
              border: 1px solid #000;
              padding: 2.4px;
              text-align: left;
            }
            table th {
              background-color: #f0f0f0;
              font-weight: bold;
              text-align: center;
              font-size: 5.6px;
            }
            .totais {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 6.4px;
              margin-top: 4px;
            }
            .declaracao-texto {
              margin-top: 6.4px;
              font-size: 5.6px;
              line-height: 1.2;
            }
            .declaracao-texto h3 {
              font-size: 7.2px;
              margin: 4px 0;
            }
            .assinatura {
              margin-top: 8px;
              display: grid;
              grid-template-columns: 2fr 1fr;
              gap: 6.4px;
            }
            .data {
              border-bottom: 1px solid #000;
              min-height: 12px;
              font-size: 6.4px;
            }
            .observacao {
              margin-top: 6.4px;
              font-size: 5.6px;
              font-weight: bold;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="etiqueta">
              <img src="${etiquetaUrl}" alt="Etiqueta" />
            </div>
            
            <div class="declaracao">
            <h1>DECLARAÇÃO DE CONTEÚDO</h1>
            
            <div class="remetente-destinatario">
              <div class="coluna">
                <h3>REMETENTE</h3>
                <div class="campo">
                  <label>NOME:</label>
                  <div class="valor">${remetente.nome}</div>
                </div>
                <div class="campo">
                  <label>ENDEREÇO:</label>
                  <div class="valor">${remetente.endereco}</div>
                </div>
                <div class="campo">
                  <label>CIDADE:</label>
                  <div class="valor">${remetente.cidade}</div>
                </div>
                <div class="campo">
                  <label>UF:</label>
                  <div class="valor">${remetente.uf}</div>
                </div>
                <div class="campo">
                  <label>CEP:</label>
                  <div class="valor">${remetente.cep}</div>
                </div>
                <div class="campo">
                  <label>CPF/CNPJ:</label>
                  <div class="valor"></div>
                </div>
              </div>
              
              <div class="coluna">
                <h3>DESTINATÁRIO</h3>
                <div class="campo">
                  <label>NOME:</label>
                  <div class="valor">${nomeDestinatario}</div>
                </div>
                <div class="campo">
                  <label>ENDEREÇO:</label>
                  <div class="valor">${destinatario.address || enderecoDest}</div>
                </div>
                <div class="campo">
                  <label>CIDADE:</label>
                  <div class="valor">${destinatario.city || ''}</div>
                </div>
                <div class="campo">
                  <label>UF:</label>
                  <div class="valor">${destinatario.province || ''}</div>
                </div>
                <div class="campo">
                  <label>CEP:</label>
                  <div class="valor">${cepFormatado}</div>
                </div>
                <div class="campo">
                  <label>CPF/CNPJ:</label>
                  <div class="valor"></div>
                </div>
              </div>
            </div>
            
            <div class="identificacao-bens">
              <h3>IDENTIFICAÇÃO DOS BENS</h3>
              <table>
                <thead>
                  <tr>
                    <th>ITEM</th>
                    <th>CONTEÚDO</th>
                    <th>QUANT.</th>
                    <th>VALOR</th>
                  </tr>
                </thead>
                <tbody>
                  ${produtos.map((produto, index) => `
                    <tr>
                      <td style="text-align: center;">${index + 1}</td>
                      <td>${produto.name}</td>
                      <td style="text-align: center;">${produto.quantity}</td>
                      <td style="text-align: right;">R$ ${produto.price.toFixed(2)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
              
              <div class="totais">
                <div class="campo">
                  <label>TOTAIS:</label>
                  <div class="valor">R$ ${totalValor.toFixed(2)}</div>
                </div>
                <div class="campo">
                  <label>PESO TOTAL (kg):</label>
                  <div class="valor"></div>
                </div>
              </div>
            </div>
            
            <div class="declaracao-texto">
              <h3>DECLARAÇÃO</h3>
              <p>
                Declaro que não me enquadro na definição de contribuinte prevista no art. 4º da Lei Complementar nº 87/1996, 
                não circulo mercadorias de forma habitual ou com intuito comercial, estou isento de emissão de nota fiscal, 
                e me responsabilizo por informações falsas.
              </p>
              <p>
                Declaro que o remetente não está postando nenhum conteúdo inflamável, explosivo, de combustão espontânea, 
                tóxico, corrosivo, gás ou qualquer outro conteúdo listado como proibido ou restrito no site dos Correios. 
                <a href="http://www.correios.com.br/precisa-de-ajuda/proibicoes-e-restricoes/proibicoes-e-restricoes" target="_blank">
                  http://www.correios.com.br/precisa-de-ajuda/proibicoes-e-restricoes/proibicoes-e-restricoes
                </a>
              </p>
            </div>
            
            <div class="assinatura">
              <div class="data">
                ${dia} de ${mesNome} de ${ano}
              </div>
              <div>
                <label>Assinatura do Declarante/Remetente:</label>
                <div style="border-bottom: 1px solid #000; min-height: 40px; margin-top: 5px;"></div>
              </div>
            </div>
            
            <div class="observacao">
              <strong>OBSERVAÇÃO:</strong> Constitui crime contra a ordem tributária suprimir ou reduzir tributo, 
              ou contribuição social e qualquer acessório (Lei 8.137/90 Art. 1°, V).
            </div>
          </div>
          </div>
        </body>
      </html>
    `;
  };

  /**
   * Impressão: etiqueta (JPEG ME) + DACE eletrônica quando a API retorna URL;
   * senão mantém a declaração de conteúdo em HTML (legado).
   */
  const gerarHtmlImpressaoEtiqueta = (pedido, etiquetaUrl, daceUrl) => {
    if (daceUrl) {
      return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Etiqueta e DACE</title>
          <style>
            @page {
              size: A4 portrait;
              margin: 10mm;
            }
            @media print {
              @page {
                size: A4 portrait;
              }
              body { margin: 2%; padding: 0; }
            }
            body {
              font-family: Arial, sans-serif;
              font-size: 8px;
              margin: 2%;
              padding: 0;
            }
            .container {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 8px;
              max-height: 40vh;
              align-items: start;
            }
            .etiqueta {
              max-height: 40vh;
              overflow: hidden;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: flex-start;
            }
            .etiqueta img {
              max-width: 100%;
              max-height: 40vh;
              height: auto;
              object-fit: contain;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="etiqueta">
              <img src="${etiquetaUrl}" alt="Etiqueta de envio" />
            </div>
            <div class="etiqueta">
              <img src="${daceUrl}" alt="Declaração de conteúdo eletrônica (DACE)" />
            </div>
          </div>
        </body>
      </html>
    `;
    }
    return gerarDeclaracaoConteudo(pedido, etiquetaUrl);
  };

  const aguardarImagensEImprimir = (printWindow) => {
    setTimeout(() => {
      const imgs = printWindow.document.querySelectorAll('img');
      let loaded = 0;
      const total = imgs.length;

      const tentarImprimir = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            setTimeout(() => printWindow.close(), 100);
          };
        }, 300);
      };

      if (total === 0) {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            setTimeout(() => printWindow.close(), 100);
          };
        }, 500);
        return;
      }

      imgs.forEach((img) => {
        if (img.complete) {
          loaded++;
          if (loaded === total) tentarImprimir();
        } else {
          img.onload = () => {
            loaded++;
            if (loaded === total) tentarImprimir();
          };
          img.onerror = () => {
            loaded++;
            if (loaded === total) tentarImprimir();
          };
        }
      });
    }, 100);
  };

  const toggleSelecaoPedido = (shopifyOrderId) => {
    setPedidosSelecionados(prev => {
      const novo = new Set(prev);
      if (novo.has(shopifyOrderId)) {
        novo.delete(shopifyOrderId);
      } else {
        novo.add(shopifyOrderId);
      }
      return novo;
    });
  };

  const imprimirMultiplasEtiquetas = async () => {
    if (pedidosSelecionados.size === 0) return;

    setImprimindoMultiplas(true);

    try {
      const pedidosIds = Array.from(pedidosSelecionados);
      const resultados = [];

      // Busca todas as etiquetas
      for (const shopifyOrderId of pedidosIds) {
        try {
          const response = await axios.get(`/api/melhor-envio/pedido/${shopifyOrderId}/etiqueta`);
          if (response.data.success) {
            resultados.push({
              pedido: response.data.pedido,
              url: response.data.url,
              daceUrl: response.data.daceUrl || null
            });
          }
        } catch (err) {
          console.error(`Erro ao buscar etiqueta do pedido ${shopifyOrderId}:`, err);
        }
      }

      if (resultados.length === 0) {
        alert('Nenhuma etiqueta encontrada para os pedidos selecionados');
        return;
      }

      // Cria HTML concatenado com todas as declarações e etiquetas
      const htmlCompleto = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <title>Imprimir Etiquetas</title>
            <style>
              @page {
                size: A4 portrait;
                margin: 10mm;
              }
              @media print {
                @page {
                  size: A4 portrait;
                }
                body { margin: 2%; padding: 0; }
              }
              body {
                font-family: Arial, sans-serif;
                font-size: 8px;
                margin: 2%;
                padding: 0;
              }
              .container {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 8px;
                max-height: 40vh;
                align-items: start;
                margin-bottom: 20px;
              }
              .etiqueta {
                max-height: 40vh;
                overflow: hidden;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-start;
              }
              .etiqueta img {
                max-width: 100%;
                max-height: 40vh;
                height: auto;
                object-fit: contain;
              }
              .declaracao {
                border: 2px solid #000;
                padding: 6.4px;
                max-height: 40vh;
                overflow: hidden;
                font-size: 7.2px;
              }
              h1 {
                text-align: center;
                font-size: 8.8px;
                font-weight: bold;
                margin: 4px 0 6.4px 0;
                text-transform: uppercase;
              }
              .remetente-destinatario {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 6.4px;
                margin-bottom: 6.4px;
              }
              .coluna {
                border: 1px solid #000;
                padding: 4px;
              }
              .coluna h3 {
                font-size: 7.2px;
                font-weight: bold;
                margin: 0 0 4px 0;
                text-transform: uppercase;
              }
              .campo {
                margin-bottom: 3.2px;
              }
              .campo label {
                font-weight: bold;
                display: block;
                margin-bottom: 0.8px;
                font-size: 6.4px;
              }
              .campo .valor {
                border-bottom: 1px solid #000;
                min-height: 9.6px;
                padding: 0.8px 0;
                font-size: 6.4px;
              }
              .identificacao-bens {
                margin-top: 6.4px;
              }
              .identificacao-bens h3 {
                font-size: 7.2px;
                font-weight: bold;
                margin: 0 0 4px 0;
                text-transform: uppercase;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 6.4px;
                font-size: 5.6px;
              }
              table th, table td {
                border: 1px solid #000;
                padding: 2.4px;
                text-align: left;
              }
              table th {
                background-color: #f0f0f0;
                font-weight: bold;
                text-align: center;
                font-size: 5.6px;
              }
              .totais {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 6.4px;
                margin-top: 4px;
              }
              .declaracao-texto {
                margin-top: 6.4px;
                font-size: 5.6px;
                line-height: 1.2;
              }
              .declaracao-texto h3 {
                font-size: 7.2px;
                margin: 4px 0;
              }
              .assinatura {
                margin-top: 8px;
                display: grid;
                grid-template-columns: 2fr 1fr;
                gap: 6.4px;
              }
              .data {
                border-bottom: 1px solid #000;
                min-height: 12px;
                font-size: 6.4px;
              }
              .observacao {
                margin-top: 6.4px;
                font-size: 5.6px;
                font-weight: bold;
              }
            </style>
          </head>
          <body>
            ${resultados.map((resultado, index) => {
        const htmlItem = gerarHtmlImpressaoEtiqueta(
          resultado.pedido,
          resultado.url,
          resultado.daceUrl
        );
        // Extrai apenas o conteúdo do body
        const bodyMatch = htmlItem.match(/<body[^>]*>([\s\S]*)<\/body>/i);
        const bodyContent = bodyMatch ? bodyMatch[1] : '';
        return `
                <div style="margin-bottom: 20px;">
                  ${bodyContent}
                </div>
              `;
      }).join('')}
          </body>
        </html>
      `;

      // Abre janela e imprime
      const printWindow = window.open('', '_blank');
      printWindow.document.write(htmlCompleto);
      printWindow.document.close();

      // Aguarda carregamento das imagens
      setTimeout(() => {
        const imgs = printWindow.document.querySelectorAll('img');
        let loaded = 0;
        const total = imgs.length;

        if (total === 0) {
          setTimeout(() => {
            printWindow.print();
            printWindow.onafterprint = () => {
              setTimeout(() => printWindow.close(), 100);
            };
          }, 500);
          return;
        }

        imgs.forEach(img => {
          if (img.complete) {
            loaded++;
            if (loaded === total) {
              setTimeout(() => {
                printWindow.print();
                printWindow.onafterprint = () => {
                  setTimeout(() => printWindow.close(), 100);
                };
              }, 300);
            }
          } else {
            img.onload = () => {
              loaded++;
              if (loaded === total) {
                setTimeout(() => {
                  printWindow.print();
                  printWindow.onafterprint = () => {
                    setTimeout(() => printWindow.close(), 100);
                  };
                }, 300);
              }
            };
          }
        });
      }, 500);

      // Limpa seleção após imprimir
      setPedidosSelecionados(new Set());

    } catch (err) {
      console.error('Erro ao imprimir múltiplas etiquetas:', err);
      alert(`Erro: ${err.message}`);
    } finally {
      setImprimindoMultiplas(false);
    }
  };

  const imprimirEtiqueta = async (shopifyOrderId) => {
    setImprimindoEtiqueta(prev => ({ ...prev, [shopifyOrderId]: true }));

    try {
      const response = await axios.get(`/api/melhor-envio/pedido/${shopifyOrderId}/etiqueta`);

      if (response.data.success && response.data.url) {
        // Cria uma nova janela para impressão automática
        const printWindow = window.open('', '_blank');

        const html = gerarHtmlImpressaoEtiqueta(
          response.data.pedido,
          response.data.url,
          response.data.daceUrl || null
        );

        printWindow.document.write(html);
        printWindow.document.close();

        aguardarImagensEImprimir(printWindow);
      } else {
        alert('Erro ao obter etiqueta');
      }
    } catch (err) {
      console.error('Erro ao imprimir etiqueta:', err);
      const errorMsg = err.response?.data?.error || 'Erro ao buscar etiqueta';
      alert(`Erro: ${errorMsg}`);
    } finally {
      setImprimindoEtiqueta(prev => ({ ...prev, [shopifyOrderId]: false }));
    }
  };

  const marcarComoEnviado = async (shopifyOrderId) => {
    setMarcandoEnviado(prev => ({ ...prev, [shopifyOrderId]: true }));

    try {
      await axios.patch(`/api/orders/${shopifyOrderId}/marcar-enviado`);

      // Remove da lista atual se estiver na página de pedidos para embalar
      if (currentView === 'pedidos-a-enviar') {
        setOrders(prevOrders => prevOrders.filter(order => order.shopify_order_id !== shopifyOrderId));
      }
    } catch (err) {
      console.error('Erro ao marcar como enviado:', err);
      alert(`Erro ao marcar como enviado: ${err.response?.data?.error || err.message}`);
    } finally {
      setMarcandoEnviado(prev => ({ ...prev, [shopifyOrderId]: false }));
    }
  };

  const reverterEnvio = async (shopifyOrderId) => {
    setRevertendoEnvio(prev => ({ ...prev, [shopifyOrderId]: true }));

    try {
      await axios.patch(`/api/orders/${shopifyOrderId}/reverter-envio`);

      // Remove da lista atual se estiver na página de enviados
      if (currentView === 'pedidos-enviados') {
        setOrders(prevOrders => prevOrders.filter(order => order.shopify_order_id !== shopifyOrderId));
      }
    } catch (err) {
      console.error('Erro ao reverter envio:', err);
      alert(`Erro ao reverter envio: ${err.response?.data?.error || err.message}`);
    } finally {
      setRevertendoEnvio(prev => ({ ...prev, [shopifyOrderId]: false }));
    }
  };

  useEffect(() => {
    if (user && !authLoading) {
      if (currentView === 'pedidos-a-enviar') {
        fetchOrders('not_sent');
      } else if (currentView === 'pedidos-enviados') {
        fetchOrders('Enviado');
      }
    }
  }, [currentView, user, authLoading, fetchOrders]);

  const hasIgnoreTag = (order) => {
    let raw = {};
    if (order.raw_data) {
      if (typeof order.raw_data === 'object') {
        raw = order.raw_data;
      } else {
        try {
          raw = JSON.parse(order.raw_data);
        } catch {
          raw = {};
        }
      }
    }

    const tags = raw.tags;
    if (!tags) return false;

    const normalize = (val) => String(val).trim().toLowerCase();

    const arr = Array.isArray(tags)
      ? tags.map(t => (typeof t === 'object' && t !== null && t.tag != null ? t.tag : t))
      : String(tags).split(',').map(t => t && t.trim()).filter(Boolean);

    for (const t of arr) {
      const lower = normalize(t);
      if (lower === 'ignorar') {
        console.log('[DEBUG hasIgnoreTag] Ignorando pedido', {
          id: order.id,
          order_number: order.order_number,
          shopify_order_id: order.shopify_order_id,
          tags: arr
        });
        return true;
      }
    }

    return false;
  };

  const getFirstName = (fullName) => {
    if (!fullName) return '';
    const parts = String(fullName).trim().split(/\s+/);
    if (parts.length === 0) return '';
    const first = parts[0].toLowerCase();
    return first.charAt(0).toUpperCase() + first.slice(1);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  const addBusinessDays = (date, businessDays) => {
    // Garante que estamos trabalhando só com a parte de data (ignorando horário)
    const base = new Date(date);
    const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
    let added = 0;

    // Avança em dias, só contando segunda a sexta como dias úteis
    while (added < businessDays) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay(); // 0 domingo, 6 sábado
      if (day >= 1 && day <= 5) {
        added++;
      }
    }

    return d;
  };

  const formatDatePtBr = (date) => {
    try {
      return new Date(date).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return '';
    }
  };

  /** Mesma regra da mensagem WhatsApp: 10 dias úteis após a data do pedido. */
  const getPrazoEnvioPedido = (order) => {
    const dataPedido = order.created_at ? new Date(order.created_at) : new Date();
    const dataLimite = addBusinessDays(dataPedido, 10);
    const dataLimiteFmt = formatDatePtBr(dataLimite);
    const hoje = new Date();
    const inicioHoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
    const inicioLimite = new Date(
      dataLimite.getFullYear(),
      dataLimite.getMonth(),
      dataLimite.getDate()
    );
    let alertaPrazo = null;
    if (inicioHoje > inicioLimite) alertaPrazo = 'atrasado';
    else if (inicioHoje.getTime() === inicioLimite.getTime()) alertaPrazo = 'hoje';
    return { dataLimiteFmt, alertaPrazo };
  };

  const inferPhoneFromOrder = (order) => {
    let raw = {};
    if (order.raw_data) {
      if (typeof order.raw_data === 'object') {
        raw = order.raw_data;
      } else {
        try {
          raw = JSON.parse(order.raw_data);
        } catch {
          raw = {};
        }
      }
    }

    const shipping = raw.shipping_address || raw.shipping || {};
    const billing = raw.billing_address || raw.billing || {};

    const candidate =
      shipping.phone ||
      shipping.tel ||
      billing.phone ||
      billing.tel ||
      raw.phone ||
      raw.telefone ||
      order.customer_phone ||
      null;

    if (!candidate) return '';
    const digits = String(candidate).replace(/\D/g, '');
    if (!digits) return '';
    if (digits.startsWith('55')) return digits;
    if (digits.length === 11) return `55${digits}`;
    return digits;
  };

  const abrirModalMensagem = (order) => {
    // Nome do cliente
    const nomeCliente =
      (order.shipping_address && order.shipping_address.name) ||
      order.customer_name ||
      '';
    const primeiroNome = getFirstName(nomeCliente);

    // Data estimada (10 dias úteis a partir da data do pedido)
    const dataPedido = order.created_at ? new Date(order.created_at) : new Date();
    const dataEstimativa = addBusinessDays(dataPedido, 10);
    const dataTexto = formatDatePtBr(dataEstimativa);

    const saudacao = getGreeting();

    const mensagem = `*[PEDIDO CONFIRMADO]*

*${saudacao}, ${primeiroNome || 'tudo bem'}, tudo bem?*

*Aqui é o Ismael, da Fut&Arte Quadros.*

Passando só pra confirmar que o pagamento do seu pedido foi *aprovado*, e que em breve ele entrará em produção. 
Como informado no processo da compra, há um *prazo de produção em média de 10 dias úteis*, o que dará no dia *${dataTexto}*, e então ele será *enviado*. Sempre faço de tudo pra enviar o quanto antes, mas como aqui praticamente tudo é feito somente por mim nem sempre é possível, então conto com a sua compreensão.

Enquanto isso, qualquer dúvida estou à *disposição*.

*Grande abraço!*`;

    const numeroInicial = inferPhoneFromOrder(order);

    setMensagemModal({
      aberto: true,
      order,
      numero: numeroInicial,
      mensagem,
      sending: false,
      error: null,
      success: null
    });
  };

  const fecharModalMensagem = () => {
    setMensagemModal({
      aberto: false,
      order: null,
      numero: '',
      mensagem: '',
      sending: false,
      error: null,
      success: null
    });
  };

  const enviarMensagemWhatsapp = async () => {
    if (!mensagemModal.numero || !mensagemModal.mensagem) {
      setMensagemModal(prev => ({
        ...prev,
        error: 'Preencha o número e a mensagem antes de enviar.'
      }));
      return;
    }

    try {
      setMensagemModal(prev => ({
        ...prev,
        sending: true,
        error: null,
        success: null
      }));

      await axios.post('/api/whatsapp/send', {
        number: mensagemModal.numero,
        message: mensagemModal.mensagem,
        orderId: mensagemModal.order?.id
      });

      setMensagemModal(prev => ({
        ...prev,
        sending: false,
        success: 'Mensagem enviada com sucesso!'
      }));
    } catch (err) {
      console.error('Erro ao enviar mensagem WhatsApp:', err);
      setMensagemModal(prev => ({
        ...prev,
        sending: false,
        error: err.response?.data?.error || 'Erro ao enviar mensagem. Tente novamente.'
      }));
    }
  };

  const getOrderTagsInfo = (order) => {
    let raw = {};
    if (order.raw_data) {
      if (typeof order.raw_data === 'object') {
        raw = order.raw_data;
      } else {
        try {
          raw = JSON.parse(order.raw_data);
        } catch {
          raw = {};
        }
      }
    }
    let prioridade = false;
    let noteText = null;

    // PRIORIDADE continua vindo das tags
    const tags = raw.tags;
    if (tags) {
      const arr = Array.isArray(tags)
        ? tags
        : String(tags).split(',').map(t => t && t.trim()).filter(Boolean);

      for (const t of arr) {
        const val = typeof t === 'object' && t !== null && t.tag != null ? String(t.tag) : String(t);
        const lower = val.toLowerCase();
        if (lower === 'prioridade') {
          prioridade = true;
        }
      }
    }

    // NOTES: prioridade é o order_comment vindo do CartPanda (salvo em raw_data.order_comment)
    const fromVal = (val) => {
      if (!val) return null;
      const s = String(val).trim();
      const m = s.match(/^note\s*:(.*)$/i);
      return (m ? m[1] : s).trim() || null;
    };

    if (raw.order_comment || order.order_comment) {
      noteText = fromVal(raw.order_comment || order.order_comment);
    } else {
      // fallback: note_attributes (se usado) ou note simples
      const noteAttrs = raw.note_attributes;
      if (Array.isArray(noteAttrs)) {
        for (const attr of noteAttrs) {
          const val = (attr?.value || '').toString().trim();
          if (val) {
            noteText = fromVal(val);
            if (noteText) break;
          }
        }
      } else if (order.note || raw.note) {
        noteText = fromVal(order.note || raw.note) || noteText;
      }
    }

    return { prioridade, noteText };
  };

  const NORMALIZACAO_ARTE_DE_PARA = {
    "D'ale": 'Dalessandro'
  };

  const extractEncomendaItem = (product) => {
    const originalName = (product?.name || product?.title || '').toString();
    const name = originalName.replace(/\s+/g, ' ').trim();

    const tamanhoMatch = name.match(/\b(A3|A4)\b/i);
    const tamanho = tamanhoMatch ? tamanhoMatch[1].toUpperCase() : 'N/A';

    const corMatch = name.match(/\b(Dourada|Preta|Branca|Azul Escuro)\b/i);
    const cor = corMatch ? corMatch[1].replace(/\b\w/g, (c) => c.toUpperCase()) : 'N/A';

    let arte = name.replace(/^Quadro\s+/i, '').trim();
    arte = arte.replace(/\s+(Dourada|Preta|Branca|Azul Escuro)\s*\/.*$/i, '').trim();
    arte = arte.replace(/\s*:\s*.*/i, '').trim();
    arte = arte.replace(/,\s*.*/i, '').trim();
    arte = arte.replace(/\s+"[^"]*".*$/i, '').trim();
    arte = arte || 'Sem nome';

    if (NORMALIZACAO_ARTE_DE_PARA[arte]) {
      arte = NORMALIZACAO_ARTE_DE_PARA[arte];
    }

    const quantidade = parseInt(product?.quantity, 10) || 1;

    return { arte, tamanho, cor, quantidade };
  };

  const gerarListaEncomenda = () => {
    const visibleOrders = orders.filter(o => !hasIgnoreTag(o));
    const grouped = new Map();
    const pedidosShopifyIds = new Set();

    visibleOrders.forEach((order) => {
      const tagsInfo = getOrderTagsInfo(order);
      if (tagsInfo?.noteText && tagsInfo.noteText.toLowerCase().includes('encomendado')) {
        return;
      }

      const products = Array.isArray(order.products) ? order.products : [];
      if (!products.length) return;

      pedidosShopifyIds.add(order.shopify_order_id);
      const orderRef = String(order.order_number || order.shopify_order_id);

      products.forEach((product) => {
        const item = extractEncomendaItem(product);
        const key = `${item.arte}|||${item.tamanho}|||${item.cor}`;

        if (!grouped.has(key)) {
          grouped.set(key, {
            arte: item.arte,
            tamanho: item.tamanho,
            cor: item.cor,
            quantidade: 0,
            pedidos: new Set()
          });
        }

        const row = grouped.get(key);
        row.quantidade += item.quantidade;
        row.pedidos.add(orderRef);
      });
    });

    const linhas = Array.from(grouped.values())
      .map((row) => ({
        arte: row.arte,
        tamanho: row.tamanho,
        cor: row.cor,
        quantidade: row.quantidade,
        pedidos: Array.from(row.pedidos)
      }))
      .sort((a, b) =>
        a.arte.localeCompare(b.arte, 'pt-BR') ||
        a.tamanho.localeCompare(b.tamanho, 'pt-BR') ||
        a.cor.localeCompare(b.cor, 'pt-BR')
      );

    const header = 'Arte\tTamanho\tCor\tQuantidade\tPedidos';
    const body = linhas.map((row) =>
      `${row.arte}\t${row.tamanho}\t${row.cor}\t${row.quantidade}\t${row.pedidos.join('\t')}`
    );
    const textoPlanilha = [header, ...body].join('\n');

    setEncomendaModal({
      aberto: true,
      linhas,
      textoPlanilha,
      pedidosShopifyIds: Array.from(pedidosShopifyIds),
      pedidosCount: pedidosShopifyIds.size,
      loading: false,
      error: null,
      success: null
    });
  };

  const fecharEncomendaModal = () => {
    setEncomendaModal({
      aberto: false,
      linhas: [],
      textoPlanilha: '',
      pedidosShopifyIds: [],
      pedidosCount: 0,
      loading: false,
      error: null,
      success: null
    });
  };

  const copiarListaEncomenda = async () => {
    try {
      await navigator.clipboard.writeText(encomendaModal.textoPlanilha);
      setEncomendaModal(prev => ({
        ...prev,
        success: 'Lista copiada para a área de transferência.',
        error: null
      }));
    } catch {
      setEncomendaModal(prev => ({
        ...prev,
        error: 'Não foi possível copiar automaticamente. Copie manualmente o texto.',
        success: null
      }));
    }
  };

  const marcarListaComoEncomendada = async () => {
    if (!encomendaModal.pedidosShopifyIds.length) {
      setEncomendaModal(prev => ({
        ...prev,
        error: 'Nenhum pedido para marcar.',
        success: null
      }));
      return;
    }

    try {
      setEncomendaModal(prev => ({ ...prev, loading: true, error: null, success: null }));

      await axios.post('/api/orders/mark-as-ordered', {
        shopifyOrderIds: encomendaModal.pedidosShopifyIds
      });

      await fetchOrders('not_sent');

      setEncomendaModal(prev => ({
        ...prev,
        loading: false,
        success: 'Pedidos marcados como encomendados com sucesso.'
      }));
    } catch (err) {
      setEncomendaModal(prev => ({
        ...prev,
        loading: false,
        error: err.response?.data?.error || 'Erro ao marcar pedidos como encomendados.'
      }));
    }
  };

  const renderOrdersList = () => {
    const visibleOrders =
      currentView === 'pedidos-a-enviar'
        ? orders.filter(o => !hasIgnoreTag(o))
        : orders;

    console.log('[DEBUG renderOrdersList]', {
      currentView,
      totalOrders: orders.length,
      visibleOrders: visibleOrders.length
    });

    return (
      <>
        <div className="orders-count">
          <strong>{visibleOrders.length}</strong> pedido(s) encontrado(s)
          {user.is_admin && currentView === 'pedidos-a-enviar' && (
            <button
              className="btn-gerar-encomenda"
              onClick={gerarListaEncomenda}
              style={{ marginLeft: '12px' }}
            >
              Gerar lista para encomenda
            </button>
          )}
        </div>

        {visibleOrders.length === 0 ? (
          <div className="no-orders">
            Nenhum pedido encontrado
          </div>
        ) : (
          <div className="orders-list">
            {([...visibleOrders]
              .map((order, idx) => {
                const tagsInfo = getOrderTagsInfo(order);
                return { order, idx, tagsInfo };
              })
              .sort((a, b) => {
                // Prioridade primeiro, mantendo a ordem original dentro de cada grupo
                if (a.tagsInfo.prioridade && !b.tagsInfo.prioridade) return -1;
                if (!a.tagsInfo.prioridade && b.tagsInfo.prioridade) return 1;
                return a.idx - b.idx;
              })
            ).map(({ order, tagsInfo }) => {
              const prazoEnvio =
                currentView === 'pedidos-a-enviar' ? getPrazoEnvioPedido(order) : null;
              return (
            <div key={order.id} className="order-card">
              <div className="order-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="checkbox"
                    checked={pedidosSelecionados.has(order.shopify_order_id)}
                    onChange={() => toggleSelecaoPedido(order.shopify_order_id)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <h3>Pedido #{order.order_number}</h3>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {tagsInfo.prioridade && (
                    <span className="order-badge order-badge-priority">PRIORIDADE</span>
                  )}
                  {tagsInfo.noteText && (
                    <span className="order-badge order-badge-note">
                      {tagsInfo.noteText.toUpperCase()}
                    </span>
                  )}
                  {prazoEnvio?.alertaPrazo === 'hoje' && (
                    <span className="order-badge order-badge-deadline-today">
                      PRAZO DE ENVIO ENCERRA HOJE
                    </span>
                  )}
                  {prazoEnvio?.alertaPrazo === 'atrasado' && (
                    <span className="order-badge order-badge-deadline-late">ENVIO ATRASADO</span>
                  )}
                </div>
              </div>
              <div className="order-details">
                {order.customer_name && order.customer_name !== 'Cliente não identificado' && (
                  <p><strong>Cliente:</strong> {order.customer_name}</p>
                )}
                <p><strong>Data:</strong> {new Date(order.created_at).toLocaleString('pt-BR')}</p>
                {prazoEnvio && (
                  <p>
                    <strong>Data limite de envio (10 dias úteis):</strong>{' '}
                    {prazoEnvio.dataLimiteFmt}
                  </p>
                )}
                <p><strong>Itens:</strong> {Array.isArray(order.products) ? order.products.length : 0}</p>
              </div>

              {order.products && Array.isArray(order.products) && order.products.length > 0 && (
                <div className="order-section">
                  <h4>📦 Produtos:</h4>
                  <ul className="products-list">
                    {order.products.map((product, index) => {
                      const imageUrl = product.product_image_url || null;
                      const name = (product.name || product.title || '').toString();
                      // Mesma lógica do Lucro (lucroService): A3( ou / A3( ; A4( ou / A4(
                      const isA3 = /\bA3\s*\(|\/\s*A3\s*\(/i.test(name);
                      const isA4 = /\bA4\s*\(|\/\s*A4\s*\(/i.test(name);
                      const sizeLabel = isA3 ? 'GRANDE - A3' : isA4 ? 'PEQUENO - A4' : null;

                      return (
                        <li key={product.id || index} className="product-item">
                          <div className="product-image-wrap">
                            {sizeLabel ? (
                              <span className="product-size-badge">{sizeLabel}</span>
                            ) : null}
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={name || 'Produto'}
                                className="product-image"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="product-image product-image-placeholder" />
                            )}
                          </div>
                          <div className="product-info">
                            <strong>{product.name || product.title}</strong>
                            {product.quantity && product.quantity > 1 && (
                              <span className="product-quantity"> x{product.quantity}</span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              <div className="order-actions">
                <button
                  onClick={() => imprimirEtiqueta(order.shopify_order_id)}
                  disabled={imprimindoEtiqueta[order.shopify_order_id]}
                  className="btn-imprimir-etiqueta"
                >
                  {imprimindoEtiqueta[order.shopify_order_id] ? '⏳ Buscando...' : '🖨️ Imprimir Etiqueta'}
                </button>
                {user.is_admin && currentView === 'pedidos-a-enviar' && !order.whatsapp_message_sent && (
                  <button
                    onClick={() => abrirModalMensagem(order)}
                    className="btn-enviar-mensagem"
                  >
                    💬 Enviar mensagem
                  </button>
                )}
                {currentView === 'pedidos-a-enviar' && (
                  <button
                    onClick={() => marcarComoEnviado(order.shopify_order_id)}
                    disabled={marcandoEnviado[order.shopify_order_id]}
                    className="btn-marcar-enviado"
                  >
                    {marcandoEnviado[order.shopify_order_id] ? '⏳ Marcando...' : '✅ Marcar como Enviado'}
                  </button>
                )}
                {currentView === 'pedidos-enviados' && (
                  <button
                    onClick={() => reverterEnvio(order.shopify_order_id)}
                    disabled={revertendoEnvio[order.shopify_order_id]}
                    className="btn-reverter-envio"
                  >
                    {revertendoEnvio[order.shopify_order_id] ? '⏳ Revertendo...' : '↩️ Reverter Envio'}
                  </button>
                )}
              </div>
            </div>
            );
            })}
        </div>
      )}
    </>
  );
  };


  const handleNavClick = (view) => {
    if (view === 'relatorios' && !user?.is_admin) return;
    setCurrentView(view);
    // Fecha o sidebar em mobile após clicar
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  };

  // Mostra login se não estiver autenticado
  if (authLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>⏳ Carregando...</div>
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  return (
    <div className="App">
      {/* Overlay para fechar sidebar em mobile */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2>🛍️ Joga 10</h2>
          <p className="sidebar-subtitle">Admin</p>
        </div>
        <nav className="sidebar-nav">
          <button
            className={`nav-item ${currentView === 'pedidos-a-enviar' ? 'active' : ''}`}
            onClick={() => handleNavClick('pedidos-a-enviar')}
          >
            📦 Pedidos para Embalar
          </button>
          <button
            className={`nav-item ${currentView === 'pedidos-enviados' ? 'active' : ''}`}
            onClick={() => handleNavClick('pedidos-enviados')}
          >
            ✅ Pedidos Enviados
          </button>
          {user.is_admin && (
            <>
              <button
                className={`nav-item ${currentView === 'lucro' ? 'active' : ''}`}
                onClick={() => handleNavClick('lucro')}
              >
                📊 Lucro
              </button>
              <button
                className={`nav-item ${currentView === 'relatorios' ? 'active' : ''}`}
                onClick={() => handleNavClick('relatorios')}
              >
                📈 Relatórios
              </button>
              <button
                className={`nav-item ${currentView === 'usuarios' ? 'active' : ''}`}
                onClick={() => handleNavClick('usuarios')}
              >
                👥 Usuários
              </button>
            </>
          )}
        </nav>
        <div className="sidebar-footer">
          <button onClick={syncOrders} disabled={syncing} className="btn-sync-sidebar">
            {syncing ? '⏳ Sincronizando...' : '🔄 Sincronizar'}
          </button>
          {syncStatus && (
            <div className="sync-status-sidebar">
              ✅ {syncStatus.success} sincronizado(s)
            </div>
          )}
        </div>
      </div>

      <div className="main-content">
        <header className="content-header">
          <button
            className="menu-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle menu"
          >
            ☰
          </button>
          <h1>
            {currentView === 'pedidos-a-enviar' && '📦 Pedidos para Embalar'}
            {currentView === 'pedidos-enviados' && '✅ Pedidos Enviados'}
            {currentView === 'lucro' && '📊 Lucro da loja'}
            {currentView === 'relatorios' && '📈 Relatórios & Insights'}
            {currentView === 'usuarios' && '👥 Gerenciar Usuários'}
          </h1>
        </header>

        <main className="content-main">
          {error && (
            <div className="error-message">
              ❌ Erro: {error}
            </div>
          )}

          {loading && (
            <div className="loading">
              ⏳ Carregando...
            </div>
          )}

          {!loading && !error && (
            <>
              {currentView === 'usuarios' && <UserManagement />}
              {currentView === 'lucro' && <Lucro />}
              {currentView === 'relatorios' && user.is_admin && <Relatorios />}
              {(currentView === 'pedidos-a-enviar' || currentView === 'pedidos-enviados') && renderOrdersList()}
            </>
          )}
        </main>

        {mensagemModal.aberto && (
          <div className="modal-overlay">
            <div className="modal">
              <h2>Enviar mensagem por WhatsApp</h2>
              <p>
                <strong>Pedido:</strong>{' '}
                {mensagemModal.order ? `#${mensagemModal.order.order_number}` : ''}
              </p>
              <div className="form-group">
                <label>Número (com DDD, apenas números)</label>
                <input
                  type="text"
                  value={mensagemModal.numero}
                  onChange={(e) =>
                    setMensagemModal(prev => ({ ...prev, numero: e.target.value }))
                  }
                  placeholder="Ex: 5511999999999"
                />
              </div>
              <div className="form-group">
                <label>Mensagem</label>
                <textarea
                  rows={8}
                  value={mensagemModal.mensagem}
                  onChange={(e) =>
                    setMensagemModal(prev => ({ ...prev, mensagem: e.target.value }))
                  }
                />
              </div>
              {mensagemModal.error && (
                <div className="error-message">
                  {mensagemModal.error}
                </div>
              )}
              {mensagemModal.success && (
                <div className="success-message">
                  {mensagemModal.success}
                </div>
              )}
              <div className="modal-actions">
                <button
                  onClick={fecharModalMensagem}
                  disabled={mensagemModal.sending}
                  className="btn-cancelar"
                >
                  Cancelar
                </button>
                <button
                  onClick={enviarMensagemWhatsapp}
                  disabled={mensagemModal.sending}
                  className="btn-confirmar-envio"
                >
                  {mensagemModal.sending ? 'Enviando...' : 'Confirmar envio'}
                </button>
              </div>
            </div>
          </div>
        )}

        {encomendaModal.aberto && (
          <div className="modal-overlay">
            <div className="modal modal-encomenda">
              <h2>Lista para encomenda</h2>
              <p>
                {encomendaModal.linhas.length} item(ns) agrupado(s) em {encomendaModal.pedidosCount} pedido(s).
              </p>

              <div className="form-group">
                <label>Pronto para copiar e colar na planilha</label>
                <textarea
                  rows={12}
                  value={encomendaModal.textoPlanilha}
                  readOnly
                />
              </div>

              {encomendaModal.error && (
                <div className="error-message">
                  {encomendaModal.error}
                </div>
              )}
              {encomendaModal.success && (
                <div className="success-message">
                  {encomendaModal.success}
                </div>
              )}

              <div className="modal-actions">
                <button
                  onClick={fecharEncomendaModal}
                  disabled={encomendaModal.loading}
                  className="btn-cancelar"
                >
                  Fechar
                </button>
                <button
                  onClick={copiarListaEncomenda}
                  disabled={encomendaModal.loading || !encomendaModal.textoPlanilha}
                  className="btn-copiar-encomenda"
                >
                  Copiar lista
                </button>
                <button
                  onClick={marcarListaComoEncomendada}
                  disabled={encomendaModal.loading || !encomendaModal.pedidosShopifyIds.length}
                  className="btn-marcar-encomendado"
                >
                  {encomendaModal.loading ? 'Marcando...' : 'Marcar como encomendado'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Botão flutuante para imprimir múltiplas etiquetas */}
        {pedidosSelecionados.size > 0 && (
          <button
            className="btn-flutuante-imprimir"
            onClick={imprimirMultiplasEtiquetas}
            disabled={imprimindoMultiplas}
          >
            {imprimindoMultiplas ? '⏳ Imprimindo...' : `🖨️ Imprimir Etiquetas (${pedidosSelecionados.size})`}
          </button>
        )}
      </div>
    </div>
  );
}

export default App;
